/**
 * @file        src/layout/AppLayout.jsx
 * @description Top-level layout: fixed right sidebar + mobile toggle/backdrop +
 *              outlet for the active route. Mirrors the design HTML's
 *              <aside id="sidebar">, the mobile hamburger, and the
 *              `body.nav-collapsed` class toggling padding-right.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-09-10 Claude — Mounts <MobileBrandBar> ahead of the routed page, so every
 *                          mobile screen opens with the Neon Holidays wordmark. On a
 *                          phone the sidebar is off-canvas, so nothing named the site
 *                          on any screen. The bar is in NORMAL FLOW, never fixed: one
 *                          mount serves every route, no page reserves room for it and
 *                          none can be overlapped by it. Skipped on /voucher/* and
 *                          /invoice — those are printable documents with their own
 *                          letterhead, not screens.
 *   - 2026-07-16 Claude — Mobile: expose the Light/Dark toggle as a persistent
 *                          top-right mini button (mirrors the top-left hamburger),
 *                          so theme is reachable without opening the sidebar drawer
 *                          — where the toggle was otherwise buried on mobile.
 *   - 2026-06-22 Claude — Initial.
 */
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar.jsx';
import MobileNavButton from './MobileNavButton.jsx';
import MobileBrandBar from '../components/MobileBrandBar.jsx';
import NavBackdrop from './NavBackdrop.jsx';
import NavTooltip from './NavTooltip.jsx';
import { useBooking } from '../store/booking.store.js';

// Routes that are DOCUMENTS, not screens: an e-ticket voucher and an invoice carry
// their own letterhead, are laid out to be printed, and must not gain a second
// piece of site chrome above them. Everything else gets the mobile brand bar.
const NO_BRAND_BAR = [/^\/voucher\//, /^\/invoice(\/|$)/];

export default function AppLayout({ children }) {
  const navCollapsed = useBooking((s) => s.navCollapsed);
  const { pathname } = useLocation();
  const showBrandBar = !NO_BRAND_BAR.some((re) => re.test(pathname));

  useEffect(() => {
    document.body.classList.toggle('nav-collapsed', navCollapsed);
  }, [navCollapsed]);

  return (
    <>
      <MobileNavButton />
      {/* The Light/Dark toggle lives in the sidebar menu only. It was also mirrored
          as a fixed top-right mini button on mobile, but that duplicated the control
          and overlapped the home content, so it was removed — theme is changed from
          the menu drawer. */}
      <NavBackdrop />
      <NavTooltip />
      <Sidebar />
      {/* Mounted HERE rather than wired into each page: one mount covers every
          screen, and because the bar is in normal flow (never fixed) it simply
          precedes whatever the route renders — no page has to reserve room for it
          and none can be overlapped by it. */}
      {showBrandBar && <MobileBrandBar />}
      {children}
    </>
  );
}
